import * as React from 'react';
import * as styles from './modal.module.scss';
import SupportTicket from '../../../models/supportTicket';
import useSupport from '../../../hooks/useSupport';
import Textbox from '../../common/textbox';
import { AppContext } from '../../../shared/app-context';
import TertiaryButton from '../../common/tertiary-button';
import SecondaryButton from '../../common/secondary-button';
import UploadFileButton from './upload-file-button';
import MaterialIcon from '@material/react-material-icon';
import { toBase64 } from '../../../utilities/utilities';
import AttachmentData from '../../../models/attachmentData';

interface Props {
  ticket: SupportTicket;
}

/**
 * @function CommentForm - Component that holds the form for the comment section of the support ticket modal
 */
const CommentForm = ({ ticket }: Props) => {
  const { addComment } = useSupport();
  const { state } = React.useContext(AppContext);

  const [comment, setComment] = React.useState('');
  const [attachments, setAttachments] = React.useState<AttachmentData[]>([]);
  const fileUploader = React.useRef(null);

  React.useEffect(() => {
    setComment('');
    setAttachments([]);
  }, [ticket.comments.length]);

  /**
   * @function submitComment - Submits a comment for the backend
   * @param event Form Event
   */
  const submitComment = (event?: React.FormEvent<HTMLFormElement>) => {
    event && event.preventDefault();
    addComment(state.clientId, ticket.issueName, { text: comment, attachments });
  };

  /**
   * @function resetComment - Resets the comment to it's default state
   */
  const resetComment = () => {
    setComment('');
    setAttachments([]);
  };

  /**
   * @function addAttachment - Adds an attachment to the state, converts attachments to base64
   * @param event Input Event
   */
  const addAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileName = event.target.files[0].name;
    toBase64(event.target.files).then(data => {
      setAttachments(files => files.concat({ fileName, data }));
    });
  };

  /**
   * @function renderAttachments - Renders the attachment name, or count if attachment count is greater than 1
   */
  const renderAttachments = () => {
    if (attachments.length === 1) {
      return (
        <div className={styles.fileName}>
          <span>{attachments[0].fileName}</span>
          <MaterialIcon title="Remove Attachment" icon="close" onClick={() => setAttachments([])} />
        </div>
      );
    } else if (attachments.length > 1) {
      return (
        <div className={styles.fileName}>
          <span>{attachments.length} files</span>
          <MaterialIcon title="Remove Attachments" icon="close" onClick={() => setAttachments([])} />
        </div>
      );
    }
    return null;
  };

  return (
    <form onSubmit={event => submitComment(event)}>
      <Textbox
        value={comment}
        className={styles.textbox}
        label="Add a comment"
        multiline={true}
        variant="outlined"
        style={{ width: '100%' }}
        onChange={event => setComment(event.target.value)}
      />
      <div className={styles.buttons}>
        <div>
          <TertiaryButton text="Clear" title="Clear" handleClick={() => resetComment()} />
          <SecondaryButton
            text="Submit"
            title="Submit Comment"
            deactive={!comment && attachments.length === 0}
            handleClick={() => submitComment()}
            styles={{ minHeight: '32px', marginLeft: '20px' }}
          />
        </div>
        <div className={styles.filePicker}>
          {renderAttachments()}
          <label htmlFor="file">
            <UploadFileButton onClick={() => fileUploader.current.click()} />
          </label>
          <input type="file" id="file" name="file" ref={fileUploader} onChange={event => addAttachment(event)} />
        </div>
      </div>
    </form>
  );
};

export default CommentForm;
